import { useState, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';

const AlquilarForm = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const form = e.currentTarget;
    const fd = new FormData(form);
    const data: Record<string, string> = {};
    fd.forEach((value, key) => { data[key] = value as string; });

    const { error } = await supabase
      .from('TALLERMECANICOKEVIN_alquiler_requests')
      .insert([{
        nombre: data.nombre ?? '',
        telefono: data.telefono ?? '',
        email: data.email ?? '',
        fecha_recogida: data.fecha_recogida ?? '',
        fecha_devolucion: data.fecha_devolucion ?? '',
        vehiculo_preferido: data.vehiculo_preferido ?? '',
        conductores_adicionales: data.conductores_adicionales ?? '0',
        lugar_recogida: data.lugar_recogida ?? '',
        combustible_preferido: data.combustible_preferido ?? '',
        comentarios: data.comentarios ?? '',
        estado: 'pendiente',
        leido: false,
      }]);

    if (!error) {
      setStatus('ok');
      form.reset();
      setCharCount(0);
    } else {
      setErrorMsg('Error al enviar. Inténtalo de nuevo.');
      setStatus('error');
    }
  };

  if (status === 'ok') {
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-4">
        <div className="w-16 h-16 flex items-center justify-center bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 text-3xl">
          <i className="ri-steering-2-line" />
        </div>
        <p className="text-white font-black text-lg uppercase tracking-wide">¡Solicitud enviada!</p>
        <p className="text-zinc-400 text-sm text-center max-w-xs">Hemos recibido tu solicitud de alquiler. Nos pondremos en contacto contigo en menos de 24 horas para confirmar disponibilidad y condiciones.</p>
        <button onClick={() => setStatus('idle')} className="mt-2 text-xs text-yellow-400 hover:text-white transition-colors cursor-pointer uppercase tracking-widest">
          Nueva solicitud
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 flex items-center justify-center bg-yellow-400 text-black">
          <i className="ri-steering-2-line text-sm" />
        </div>
        <h2 className="text-white font-black text-lg uppercase">Alquila tu coche</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-6">Elige fechas y vehículo. Te confirmamos disponibilidad y precio final sin compromiso.</p>

      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
        <div className="border-l-2 border-yellow-400/40 pl-3 mb-1">
          <p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest">Tus datos</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Nombre *</label>
            <input name="nombre" required placeholder="Tu nombre completo"
              className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Teléfono *</label>
            <input name="telefono" required type="tel" placeholder="6XX XXX XXX"
              className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Email *</label>
          <input name="email" required type="email" placeholder="tu@email.com"
            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors placeholder:text-zinc-600" />
        </div>

        <div className="border-l-2 border-yellow-400/40 pl-3 mt-5 mb-1">
          <p className="text-yellow-400/80 text-xs font-bold uppercase tracking-widest">Detalles del alquiler</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Fecha de recogida *</label>
            <input name="fecha_recogida" required type="date"
              min={new Date().toISOString().split('T')[0]}
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors cursor-pointer" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Fecha de devolución *</label>
            <input name="fecha_devolucion" required type="date"
              min={new Date().toISOString().split('T')[0]}
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors cursor-pointer" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Vehículo preferido</label>
            <select name="vehiculo_preferido"
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="">Sin preferencia</option>
              <option>VW Polo 1.0 TSI 2023</option>
              <option>Cualquier utilitario</option>
              <option>Familiar / Berlina</option>
              <option>SUV / 4x4</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Conductores adicionales</label>
            <select name="conductores_adicionales"
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="0">Solo yo</option>
              <option value="1">1 conductor adicional</option>
              <option value="2">2 conductores adicionales</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Lugar de recogida *</label>
            <select name="lugar_recogida" required
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="">Seleccionar</option>
              <option>Taller — C. Guadalimar, 7 (Ronda)</option>
              <option>Otro punto acordado</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Combustible</label>
            <select name="combustible_preferido"
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 cursor-pointer">
              <option value="">Sin preferencia</option>
              <option>Gasolina</option>
              <option>Diésel</option>
              <option>Híbrido</option>
              <option>Eléctrico</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">
            Comentarios adicionales <span className="text-zinc-600 normal-case font-normal">{charCount}/500</span>
          </label>
          <textarea name="comentarios" maxLength={500} rows={3}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 500) setCharCount(val.length);
            }}
            placeholder="Kilómetros previstos, necesidades especiales, silla infantil..."
            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-4 py-2.5 outline-none focus:border-yellow-400/50 transition-colors resize-none placeholder:text-zinc-600" />
        </div>

        <div className="bg-yellow-400/5 border border-yellow-400/15 px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-yellow-400/70 mt-0.5 flex-shrink-0" />
          <p className="text-zinc-400 text-xs leading-relaxed">
            Se requiere <strong className="text-zinc-300">carnet de conducir vigente</strong> y <strong className="text-zinc-300">DNI/Pasaporte</strong>. La fianza se gestiona en el momento de la entrega. Precio final confirmado tras revisar disponibilidad.
          </p>
        </div>

        {status === 'error' && (
          <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-3 py-2">
            <i className="ri-error-warning-line mr-1" />
            {errorMsg}
          </p>
        )}

        <button type="submit" disabled={status === 'sending'}
          className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-black text-xs uppercase tracking-widest py-3.5 hover:bg-yellow-300 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2">
          {status === 'sending'
            ? <><i className="ri-loader-4-line animate-spin" /> Enviando...</>
            : <><i className="ri-steering-2-line" /> Solicitar alquiler</>}
        </button>
      </form>
    </>
  );
};

export default AlquilarForm;
